import { safe } from '$lib/utils/string';
import type { Dictionary } from '$lib/utils/types';

export class ImmSearchParams {
    private readonly internal: URLSearchParams;

    constructor(init: URLSearchParams | ImmSearchParams | string = '') {
        if (init instanceof URLSearchParams) {
            this.internal = init;
        } else if (init instanceof ImmSearchParams) {
            this.internal = new URLSearchParams(init.toString());
        } else {
            this.internal = new URLSearchParams(init);
        }
    }

    static fromObject(obj: Dictionary<string>): ImmSearchParams {
        return ImmSearchParams.fromEntries(Object.entries(obj));
    }

    static fromEntries(entries: [string, string][]): ImmSearchParams {
        const internal = new URLSearchParams();
        for (const [n, v] of entries) {
            internal.append(n, v);
        }
        return new ImmSearchParams(internal);
    }

    clone(cb: (s: URLSearchParams) => void): ImmSearchParams {
        const newSearchParams = new URLSearchParams(this.internal.toString());
        cb(newSearchParams);
        return new ImmSearchParams(newSearchParams);
    }

    append(this: this, name: string, value: string): ImmSearchParams {
        return this.clone((params) => {
            params.append(name, value);
        });
    }

    withSet(this: this, name: string, value: string): ImmSearchParams {
        return this.clone((params) => {
            params.set(name, value);
        });
    }

    sort(this: this): ImmSearchParams {
        return this.clone((params) => params.sort());
    }

    delete(this: this, name: string, value?: string | undefined) {
        return this.clone((params) => params.delete(name, value));
    }

    entries(this: this): IterableIterator<[string, string]> {
        return this.internal.entries();
    }

    get(this: this, name: string): string | null {
        return this.internal.get(name);
    }

    getAll(this: this, name: string): readonly string[] {
        return this.internal.getAll(name);
    }

    keys(this: this): IterableIterator<string> {
        return this.internal.keys();
    }

    has(this: this, name: string, value?: string | undefined): boolean {
        return this.internal.has(name, value);
    }

    values(this: this): IterableIterator<string> {
        return this.internal.values();
    }

    toObject(this: this): Dictionary<string> {
        return Object.fromEntries([...this.entries()]);
    }

    keepNames(this: this, names: string[]): ImmSearchParams {
        const params = new URLSearchParams();
        for (let [n, v] of this.internal.entries()) {
            if (names.includes(n)) {
                params.set(n, v);
            }
        }

        return new ImmSearchParams(params);
    }

    toString(this: this): string {
        const s = this.internal.toString();
        if (s == null || s === '') {
            return '';
        }
        return safe`?${s}`;
    }
}
